/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Sebastian Hartmann <s.hartmann@techdivision.com>
 */

import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Custom useStudioSearchField
 * @param props
 */
export function useStudioSearchField(props) {
  const { maps, map } = props;
  const [clearInputVisible, setClearInputVisible] = useState(false);
  const searchReference = useRef(null);
  const autocompleteReference = useRef(null);
  const zoomLevel = 12;

  useEffect(() => {
    if (searchReference.current) {
      autocompleteReference.current = new maps.places.Autocomplete(searchReference.current);
    }
  }, [maps.places.Autocomplete, searchReference]);

  /**
     * Handle onChange event of the input field
     * @param event
     */
  const handleInputChange = (event) => {
    // Get autocomplete
    if (event.target.value) {
      autocompleteReference.current.getPlace();
    }

    const clearInputVisibility = event.target.value.trim() !== '';
    setClearInputVisible(clearInputVisibility);
  };

  /**
     * Handle the click on the search button and set new map center
     */
  const handleSubmit = (event) => {
    event.preventDefault();
    const request = {
      query: searchReference.current.value,
      fields: ['name', 'geometry'],
    };
    const service = new maps.places.PlacesService(map);
    service.findPlaceFromQuery(request, (results, status) => {
      if (status === maps.places.PlacesServiceStatus.OK && results) {
        map.setCenter(results[0].geometry.location);
        map.setZoom(zoomLevel);
      }
    });
  };

  /**
     * Handle the clearing of the input
     */
  const handleClearInput = () => {
    searchReference.current.value = '';
    setClearInputVisible(false);
  };

  /**
     * Set the new map center with the coordinates from the geolocator
     * @param position
     */
  const setNewCenter = (position) => {
    const coordinates = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };
    map.setCenter(coordinates);
    map.setZoom(zoomLevel);
  };

  /**
     * Try to get the current position of the user
     */
  const handleCurrentPosition = () => {
    if (navigator && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(setNewCenter);
    }
  };

  return {
    handleInputChange,
    handleClearInput,
    handleCurrentPosition,
    handleSubmit,
    searchReference,
    autocompleteReference,
    clearInputVisible,
  };
}

useStudioSearchField.propTypes = {
  map: PropTypes.object.isRequired,
  maps: PropTypes.object.isRequired,
};
